import "./WeatherChart.css";

import { DateTime } from "luxon";
import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import ToggleButton from "../../components/ToggleButton";
import {
  cloudinessFormat,
  humidityFormat,
  pressureFormat,
  rainFormat,
  temperatureFormat,
  windFormat,
} from "../../utils";

const tooltip = (metric, timezone) => {
  function tooltipComponent({ payload }) {
    if (!payload || payload.length === 0) return null;

    const data = payload[0].payload;

    const time = DateTime.fromMillis(data.datetime)
      .setZone(timezone)
      .toLocaleString(DateTime.DATETIME_MED);

    const temp = temperatureFormat(data.conditions.t, metric);
    const humidity = humidityFormat(data.conditions.h);
    const pressure = pressureFormat(data.conditions.p);
    const wind = windFormat(data.conditions.ws, data.conditions.wd, metric);
    const rain = rainFormat(data.conditions.r);

    return (
      <div className="tooltip">
        <pre>{time}</pre>
        {temp ? <pre>Temperature: {temp}</pre> : undefined}
        {humidity ? <pre>Humidity: {humidity}</pre> : undefined}
        {pressure ? <pre>Pressure: {pressure}</pre> : undefined}
        {rain ? <pre>Rain: {rain}</pre> : undefined}
        {wind ? <pre>Wind: {wind}</pre> : undefined}
      </div>
    );
  }

  return tooltipComponent;
};

const metricAxis = {
  temperature: {
    format: (x, metric) => temperatureFormat(x, metric, 0),
    domain: [
      (dataMin) => (dataMin - 2).toFixed(),
      (dataMax) => (dataMax + 2).toFixed(),
    ],
    key: "conditions.t",
  },
  humidity: {
    format: (x) => humidityFormat(x),
    domain: [0, 100],
    key: "conditions.h",
  },
  pressure: {
    format: (x) => pressureFormat(x),
    domain: [
      (dataMin) => (dataMin - 5).toFixed(),
      (dataMax) => (dataMax + 5).toFixed(),
    ],
    key: "conditions.p",
  },
  cloudiness: {
    format: (x) => cloudinessFormat(x),
    domain: [0, 100],
    key: "conditions.c",
  },
  wind: {
    format: (x, metric) => windFormat(x, null, metric),
    domain: [0, (dataMax) => (dataMax + 2).toFixed()],
    key: "conditions.ws",
  },
};

const WeatherChart = ({
  period,
  onSetPeriod,
  unit,
  onSetUnit,
  timezone,
  onClick,
  weather,
  chartMetric,
  onSetChartMetric,
}) => {
  const metric = unit === "metric";
  return (
    <div>
      <div className="toggle-button">
        <ToggleButton
          options={[
            { option: "metric", label: "Metric" },
            { option: "imperial", label: "Imperial" },
          ]}
          onSelect={onSetUnit}
          selected={unit}
        />
        <ToggleButton
          options={[
            { option: "day", label: "Day" },
            { option: "week", label: "Week" },
            { option: "month", label: "Month" },
          ]}
          selected={period}
          onSelect={onSetPeriod}
        />
      </div>
      <div style={{ position: "relative" }}>
        <p className="attribution">OpenWeatherMap</p>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={weather}
            margin={{
              top: 5,
              right: 20,
              left: 0,
              bottom: 5,
            }}
            onClick={onClick}
          >
            <XAxis
              type="number"
              domain={["auto", "auto"]}
              tickCount={6}
              dataKey="datetime"
              tickMargin={12}
              tickFormatter={(x) =>
                DateTime.fromMillis(x)
                  .setZone(timezone)
                  .toLocaleString(
                    period === "day"
                      ? DateTime.TIME_SIMPLE
                      : DateTime.DATETIME_SIMPLE
                  )
              }
            />
            <YAxis
              domain={metricAxis[chartMetric].domain}
              tickCount={7}
              width={100}
              tickFormatter={(x) => metricAxis[chartMetric].format(x, metric)}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={tooltip(metric, timezone)} />
            <Line
              dot={false}
              isAnimationActive={false}
              dataKey={metricAxis[chartMetric].key}
              stroke="#82ca9d"
              activeDot={{
                r: 10,
              }}
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <ToggleButton
        options={[
          { option: "temperature", label: "Temp" },
          { option: "humidity", label: "Humidity" },
          { option: "pressure", label: "Pressure" },
          { option: "cloudiness", label: "Cloud" },
          { option: "wind", label: "Wind" },
        ]}
        selected={chartMetric}
        onSelect={onSetChartMetric}
      />
    </div>
  );
};

export default WeatherChart;
